import React, { useState, useEffect, useReducer } from 'react'
import SlotEditPop from '../../popup/SlotEditPop';
import { get } from '../../common/http';
import { find, forEach, orderBy, startsWith } from 'lodash';
import { getUserInfo } from '../../common/cookie';
import { DEFAULT_PAGE_COUNT, STATE_MAP, TYPE_MAP } from '../../constant/constant';
import { getDate, getDayDiff } from '../../common/util';
import PageNavigation from '../pageNavigation/PageNavigation';
import SlotApi from '../../api/SlotApi';
import SlotExtendPop from '../../popup/SlotExtendPop';

const pageOptions = [
    { key: 30, value: 30, text: '30개씩 보기', active: true, selected: true },
    { key: 50, value: 50, text: '50개씩 보기' },
    { key: 100, value: 100, text: '100개씩 보기' },
    { key: 300, value: 300, text: '300개씩 보기' }
]

const stateOptions = [
    { key: 'A', value: 'A', text: '전체보기' },
    { key: 'P', value: 'P', text: '정상' },
    { key: 'W', value: 'W', text: '대기' },
]

const getOrderClass = (order, orderBy) => {
    if (order[orderBy] === 'desc') return 'sorttable_sorted';
    if (order[orderBy] === 'asc') return 'sorttable_sorted_reverse';
    return '';
}

const SlotList = ({ total, refreshCounts }) => {
    const [canApprove, setCanApprove] = useState(false);
    const [list, setList] = useState([]);
    const [curPage, setCurPage] = useState(1);
    const [pageCount, setPageCount] = useState(30);
    const [viewState, setViewState] = useState('');
    const [isRegOpen, setIsRegOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isExtendOpen, setIsExtendOpen] = useState(false);
    const [editData, setEditData] = useState({});
    const [isExtendDisabled, setIsExtendDisabled] = useState(true);
    const [isApproveDisabled, setIsApproveDisabled] = useState(true);
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
    const [isCheckedAll, setIsCheckedAll] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [order, setOrder] = useState({
        ID: 'asc',
        START_DATE: '',
        END_DATE: '',
    })
    const [isLoading, setIsLoading] = useState(false);

    // FUNCTION
    const init = (page = curPage, count = pageCount, keyword = '', state = viewState, orderBy = '', orderType = '') => {
        setIsLoading(true);
        SlotApi.getSlotList({ page, count, keyword, state, orderBy, orderType }).then(rslt => {
            if (rslt.data && rslt.data.list) {
                const { data: { list } } = rslt;
                // 각 아이템에 checked 속성 추가
                const listWithChecked = list.map(item => ({ ...item, checked: false }));
                setList(listWithChecked);
                setCurPage(page);
            } else {
                console.error('슬롯 목록 조회 실패:', rslt.message);
                setList([]);
            }
            setIsLoading(false);
        }).catch(error => {
            console.error('슬롯 목록 조회 오류:', error);
            setList([]);
            setIsLoading(false);
        });
    }

    // EVENT
    const handleRegisterClick = () => setIsRegOpen(true);

    const regSaved = () => {
        setIsRegOpen(false);
        init();
    }

    const editSaved = () => {
        setEditData({});
        setIsEditOpen(false);
        init();
    }

    const extendSaved = () => {
        setIsExtendOpen(false);
        init();
        refreshCounts();
    }

    const handleLinkClick = url => {
        if (!startsWith(url, 'http')) return window.open('http://' + url);
        window.open(url);
    }

    const handleEditClick = item => {
        const targets = item ? [item] : getCheckedItems(list);
        setEditData({ editTargets: targets, isOnlyUpdate: true });
        setIsEditOpen(true);
    }

    const handleExtendClick = () => {
        const targets = getCheckedItems();
        if (targets.length === 0) return alert('선택된 슬롯이 없습니다.');
        setEditData({ editTargets: targets, isOnlyExtend: true });
        setIsExtendOpen(true);
    };

    const handleApproveClick = () => {
        const targets = getCheckedItems();
        if (targets.length === 0) return alert('선택된 슬롯이 없습니다.');
        if (targets.filter(item => startsWith(item.STATE, 'W')).length !== targets.length) return alert('대기 상태의 슬롯만 승인할 수 있습니다.');
        window.confirm('승인하시겠습니까?') && SlotApi.approveSlot(targets.map(t => t.ID)).then(() => {
            init();
            refreshCounts();
        });
    };

    const handleDeleteClick = () => {
        const targets = getCheckedItems();
        window.confirm('삭제하시겠습니까?') && SlotApi.deleteSlot(targets.map(t => t.ID)).then(() => {
            const newList = list.filter(item => !targets.includes(item));
            setList(newList);
            refreshCounts();
        });
    };

    const getCheckedItems = (target = list) => {
        return target.filter(item => item.checked);
    }

    const handleItemChecked = item => {
        const newList = list.map(i => item.ID === i.ID ? { ...i, checked: !i.checked } : i);
        setList(newList);
        setIsCheckedAll(newList.every(item => item.checked));
    }

    const handlePageChange = page => {
        if (page === curPage) return;
        setCurPage(page);
        init(page);
    }

    const handleKeyDown = event => {
        if (event.keyCode === 13) {
            return init(undefined, undefined, searchKeyword);
        }
    };

    const handleSearchKeywordChange = event => {
        setSearchKeyword(event.target.value);
    }

    const handleCountSelect = (e) => {
        setPageCount(e.target.value);
    }

    const handleStateSelect = (e, data) => {
        setViewState(data.value === 'A' ? '' : data.value);
    }

    const handleCheckAllClick = () => {
        const newChecked = !isCheckedAll;
        setIsCheckedAll(newChecked);
        setList(list.map(item => ({ ...item, checked: newChecked })));
    }

    const handleOrderClick = orderBy => {
        if (isLoading) return alert('loading 중입니다.');
        const newOrder = { 
            ID: '', 
            START_DATE: '',
            END_DATE: '',
        };
        newOrder[orderBy] = order[orderBy] === 'desc' ? 'asc' : 'desc';
        setOrder(newOrder);
        init(1, undefined, undefined, undefined, orderBy, newOrder[orderBy]);
    }

    // EFFECT
    useEffect(() => {
        const userInfo = getUserInfo();
        const userType = userInfo?.userType || userInfo?.USER_TYPE;
        const isAdmin = !!userInfo?.isAdmin || userType === '001';
        const isSubAdmin = !!userInfo?.isSubAdmin || userType === '011';
        setCanApprove(isAdmin || isSubAdmin);
    }, []);

    useEffect(init, [pageCount, viewState]);

    useEffect(() => {
        const checkedItems = getCheckedItems(list);
        const isCheckedItmesExist = checkedItems.length > 0;
        const isWaitingItemsExist = checkedItems.filter(item => startsWith(item.STATE, 'W')).length === checkedItems.length;
        setIsExtendDisabled(!isCheckedItmesExist);
        setIsApproveDisabled(!isCheckedItmesExist || !isWaitingItemsExist);
        setIsDeleteDisabled(!isCheckedItmesExist);
    }, [list]);

    return (
        <>
            <div className="filter-area">
                <div className="filter-search">
                    <span>{getCheckedItems().length}개 선택</span>
                    <div className="field-search">
                        <input 
                            placeholder="번호,아이디,키워드,작업 키워드"
                            value={searchKeyword}
                            onChange={handleSearchKeywordChange}
                            onKeyDown={handleKeyDown}
                        />
                        <button type="button" onClick={() => handleKeyDown({ keyCode: 13 })}>
                            <i className="icon icon-search"></i>
                        </button>
                    </div>
                </div>
                <div className="filter-control">
                    <button type="button" className="btn" onClick={handleExtendClick}>연장 신청</button>
                    <button type="button" className="btn" onClick={() => handleEditClick()}>수정</button>
                    <button type="button" className="btn" onClick={handleDeleteClick}>삭제</button>
                    {canApprove && (
                        <button type="button" className="btn btn-primary" onClick={handleApproveClick}>승인</button>
                    )}
                </div>
            </div>

            <div className="table-area">
                <table className="sortable">
                    <colgroup>
                        <col width="40px" />
                        <col width="4%" />
                        <col width="6%" />
                        <col width="4%" />
                        <col width="15%" />
                        <col width="" />
                        <col width="20%" />
                        {/* <col width="12%" />
                        <col width="12%" /> */}
                        <col width="4%" />
                        <col width="7%" />
                        <col width="7%" />
                        <col width="4%" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="sorttable_nosort">
                                <label className="checkbox">
                                    <input type="checkbox" name="" checked={isCheckedAll} onChange={handleCheckAllClick}/>
                                    <span></span>
                                </label>
                            </th>
                            <th 
                                onClick={() => handleOrderClick('ID')}
                                className={getOrderClass(order, 'ID')}
                            >번호	
                            </th>
                            <th className="sorttable_nosort">아이디</th>
                            <th className="sorttable_nosort">상태</th>
                            <th className="sorttable_nosort els">40위 내 키워드</th>
                            <th className="sorttable_nosort els">작업 키워드</th>
                            <th className="sorttable_nosort els">상품 URL</th>
                            {/* <th className="sorttable_nosort els">아이템 ID</th>
                            <th className="sorttable_nosort els">벤더아이템 ID</th> */}
                            <th className="sorttable_nosort">작업일수</th>
                            <th
                                onClick={() => handleOrderClick('START_DATE')}
                                className={getOrderClass(order, 'START_DATE')}
                            >작업시작일</th>
                            <th
                                onClick={() => handleOrderClick('END_DATE')}
                                className={getOrderClass(order, 'END_DATE')}
                            >작업종료일</th>
                            <th className="sorttable_nosort">수정</th>
                        </tr>
                    </thead>
                    <tbody>
                    {list.map(item => (
                        <tr key={`slot_${item.ID}`}>
                            <td>
                                <input type='checkbox' checked={item.checked} onChange={() => handleItemChecked(item)} />
                            </td>
                            <td>{item.ID}</td>
                            <td>{item.USER_ID}</td>
                            <td style={{ color: item.STATE === 'W' ? 'red' : 'blue' }}>{STATE_MAP[item.STATE]}</td>
                            <td className="els" title={item.MAIN_KEYWORD}>{item.MAIN_KEYWORD}</td>
                            <td className="els" title={item.PLACE_NAME}>{item.PLACE_NAME}</td>
                            <td className="els" title={item.PLACE_URL}>{item.PLACE_URL}</td>
                            {/* <td className="els" title={item.PLACE_COMPARE_URL}>{item.PLACE_COMPARE_URL}</td>
                            <td className="els" title={item.PLACE_CODE}>{item.PLACE_CODE}</td> */}
                            <td>{item.WORK_DAYS}</td>
                            <td>{getDate(item.START_DATE)}</td>
                            <td>{getDate(item.START_DATE, item.WORK_DAYS - 1)}</td>
                            <td>
                                <a onClick={() => handleEditClick(item)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" fill="none"><path fill="#45484C" d="M0 11.077h12.923V12H0zM10.8 3.23a.89.89 0 0 0 0-1.293L9.138.277a.89.89 0 0 0-1.292 0L.923 7.2v2.954h2.954zM8.492.923l1.662 1.662-1.385 1.384-1.661-1.661zM1.846 9.231V7.569l4.616-4.615 1.661 1.661-4.615 4.616z"></path></svg>
                                </a>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                <tfoot></tfoot></table>
            </div>

            <div className="table-nav">
                <PageNavigation totalCount={total} showCount={pageCount} onChange={handlePageChange} />
                <div className="table-nav-row">
                    <div>
                        <select onChange={handleCountSelect}>
                            {pageOptions.map(option => (
                                <option key={option.key} value={option.value}>{option.text}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {isEditOpen && <SlotEditPop
                isOpen={isEditOpen}
                data={editData}
                close={() => setIsEditOpen(false)}
                save={editSaved}
            />}

            {isExtendOpen && <SlotExtendPop
                isOpen={isExtendOpen}
                data={editData}
                close={() => setIsExtendOpen(false)}
                save={extendSaved}
            />}
        </>
    )
}

export default SlotList;